/**
 * Klausuren der Schulseite (F-22 Stufe 3): kommende mit Themen und Lernplan,
 * vergangene eingeklappt, dazu das Formular fuer eine neue Klausur.
 */
import { useState } from 'react';
import { Plus } from 'lucide-react';

import { Input } from '@/ui/primitives/Input';
import { Button } from '@/ui/primitives/Button';
import { KlausurKarte } from '@/modules/school/components/KlausurKarte';
import { useKlausuren, useLernplan } from '@/modules/school/useSchool';
import { createExam } from '@/db/repositories/exams';
import { klausurDatum } from '@/domain/school/exams';
import { formatDate, today } from '@/lib/datetime';
import type { Subject } from '@/domain/types';

export function Klausuren({ faecher, nurFach }: {
  faecher: Subject[];
  /** Fachseite: nur die Klausuren dieses Fachs. */
  nurFach?: string;
}) {
  const klausuren = useKlausuren().filter((k) => !nurFach || k.subjectId === nurFach);
  const lernplan = useLernplan();
  const heute = today();
  const [vergangeneZeigen, setVergangeneZeigen] = useState(false);
  const kommend = klausuren.filter((k) => klausurDatum(k) >= heute);
  const vergangen = klausuren.filter((k) => klausurDatum(k) < heute).reverse();

  return (
    <section aria-label="Klausuren" className="flex flex-col gap-3 rounded-lg border border-line bg-surface px-4 py-3.5">
      <h2 className="text-sm font-semibold">Klausuren ({kommend.length} anstehend)</h2>
      {kommend.length === 0 && <p className="text-sm text-ink-faint">Keine anstehenden Klausuren.</p>}
      <ul className="flex flex-col gap-2">
        {kommend.map((k) => <KlausurKarte key={k.id} klausur={k} lernplan={lernplan.get(k.id) ?? []} />)}
      </ul>

      {faecher.length > 0 && <NeueKlausurForm faecher={faecher} />}

      {vergangen.length > 0 && (
        <div className="flex flex-col gap-1.5 border-t border-line pt-2">
          <button type="button" aria-expanded={vergangeneZeigen} onClick={() => setVergangeneZeigen((x) => !x)}
            className="self-start text-[12px] text-ink-faint hover:text-ink">
            {vergangeneZeigen ? 'Vergangene ausblenden' : `Vergangene Klausuren (${vergangen.length})`}
          </button>
          {vergangeneZeigen && (
            <ul className="flex flex-col gap-1 text-sm text-ink-muted">
              {vergangen.map((k) => <li key={k.id}>{formatDate(klausurDatum(k))} · {k.title}</li>)}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

function NeueKlausurForm({ faecher }: { faecher: Subject[] }) {
  const [fach, setFach] = useState('');
  const [titel, setTitel] = useState('');
  const [datum, setDatum] = useState('');
  const [von, setVon] = useState('08:00');
  const [bis, setBis] = useState('09:30');
  const [raum, setRaum] = useState('');
  const fachId = fach || faecher[0]!.id;
  const fachName = faecher.find((f) => f.id === fachId)?.name ?? '';
  const gueltig = !!datum && von < bis;

  async function anlegen() {
    if (!gueltig) return;
    if (await createExam({ subjectId: fachId, titel, datum, von, bis, raum })) {
      setTitel(''); setDatum(''); setRaum('');
    }
  }

  const feld = 'h-9 rounded-md border border-line bg-surface px-2 text-sm';
  return (
    <div className="flex flex-wrap items-end gap-2 border-t border-line pt-2.5">
      <select value={fachId} onChange={(e) => setFach(e.target.value)} aria-label="Fach der Klausur" className={feld}>
        {faecher.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
      </select>
      <div className="min-w-40 flex-1">
        <Input value={titel} onChange={(e) => setTitel(e.target.value)} placeholder={`Klausur ${fachName}`}
          aria-label="Titel der Klausur" className="h-9 text-sm" />
      </div>
      <div className="w-40"><Input type="date" value={datum} min={today()} onChange={(e) => setDatum(e.target.value)} aria-label="Datum der Klausur" className="h-9 text-sm" /></div>
      <div className="w-32"><Input type="time" value={von} onChange={(e) => setVon(e.target.value)} aria-label="Beginn der Klausur" className="h-9 text-sm" /></div>
      <div className="w-32"><Input type="time" value={bis} onChange={(e) => setBis(e.target.value)} aria-label="Ende der Klausur" className="h-9 text-sm" /></div>
      <div className="w-28"><Input value={raum} onChange={(e) => setRaum(e.target.value)} placeholder="Raum" aria-label="Raum der Klausur" className="h-9 text-sm" /></div>
      <Button size="sm" variant="primary" disabled={!gueltig} onClick={() => void anlegen()}>
        <Plus className="size-3.5" /> Klausur
      </Button>
    </div>
  );
}
